/**
 * The shapes behind the companies, projects and Employees surfaces (PRD §4.3, §4.4, §5.1).
 *
 * Same schema on the form and on the route, so a field the client rejects is a field the API
 * rejects. Structure and bounds only — the invariants I1–I5 need the database and live in
 * `src/server/services/invariants.ts`.
 */

import { z } from "zod";
import { RD_SHARE_LIMITS_DEFAULT, type RdShareLimits } from "../rd-share-limits";
import { emailSchema } from "./auth";
import {
  absenceFractionSchema,
  absenceTypeSchema,
  businessDateSchema,
  uuidSchema,
} from "./tracking";

/** The same 16 codes the `companies.bundesland` CHECK holds (PRD §6.3). */
export const BUNDESLAND_CODES = [
  "BW",
  "BY",
  "BE",
  "BB",
  "HB",
  "HH",
  "HE",
  "MV",
  "NI",
  "NW",
  "RP",
  "SL",
  "SN",
  "ST",
  "SH",
  "TH",
] as const;

export type Bundesland = (typeof BUNDESLAND_CODES)[number];

/** The reference normalizes NRW → NW before validating; so do we (PRD §6.3). */
export const bundeslandSchema = z.preprocess(
  (value) => (typeof value === "string" && value.trim().toUpperCase() === "NRW" ? "NW" : value),
  z.enum(BUNDESLAND_CODES),
);

const nameSchema = z.string().trim().min(1).max(200);
const personNameSchema = z.string().trim().min(1).max(100);

/**
 * Optional Position (Excel column "Position"; Stundenzettel W7). Empty / whitespace → null.
 * Non-null values are trimmed and length-checked (matches `users.position` CHECK).
 * Not the access Rolle (`users.role` — employee / company_admin).
 */
export const positionSchema = z.preprocess((value) => {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}, z.string().min(1).max(200).nullable());

/** Optional Tätigkeitsbeschreibung (Excel column 5). Empty / whitespace → null; ends trimmed only. */
export const JOB_DESCRIPTION_MAX = 2000;

export const jobDescriptionSchema = z.preprocess((value) => {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (typeof value !== "string") return value;
  const normalized = value
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/^\s+|\s+$/g, "");
  return normalized === "" ? null : normalized;
}, z.string().min(1).max(JOB_DESCRIPTION_MAX).nullable());

/**
 * R&D share crosses the wire as a fraction, because that is what the column stores and what
 * `assignmentInputSchema` already speaks (PLAN.md D-28). Percent belongs to the UI and to the
 * import sheet, and is converted at those two edges only.
 *
 * Absolute wire band is 0..1; the `app_settings.rd_share_limits` percent band (default
 * 1%…95%) is enforced in `assertRdShareWithinLimits` so limits stay tunable without
 * baking 0.95 into zod/DB CHECK.
 */
export const rdShareSchema = z.number().min(0).max(1);

export function percentToShare(percent: number): number {
  return Math.round(percent * 10) / 1000;
}

export function shareToPercent(share: number): number {
  return Math.round(share * 1000) / 10;
}

/**
 * How much R&D share (0..1) a person still has available across active assignments.
 * Budget is the configured `rd_share_limits.maxPercent` (default 95%), not 100%.
 * When editing one assignment, pass its id so that row is not counted against the budget.
 */
export function remainingRdShare(
  assignments: ReadonlyArray<{ id: string; rdShare: number; archived: boolean }>,
  excludeAssignmentId?: string | null,
  limits: RdShareLimits = RD_SHARE_LIMITS_DEFAULT,
): number {
  const maxShare = limits.maxPercent / 100;
  const used = assignments
    .filter((entry) => !entry.archived && entry.id !== excludeAssignmentId)
    .reduce((sum, entry) => sum + entry.rdShare, 0);
  return Math.max(0, maxShare - used);
}

// --------------------------------------------------------------------- companies

export const companyCreateSchema = z.object({
  name: nameSchema,
  bundesland: bundeslandSchema,
});

export const companyPatchSchema = companyCreateSchema
  .partial()
  .refine((patch) => Object.keys(patch).length > 0, { message: "Nothing to change" });

// --------------------------------------------------------------------- projects

/**
 * BSFZ Bescheid "Lfd. Nr." (short form), e.g. `666-969-422/2026-1`.
 * Optional on the wire; empty/whitespace becomes null. When set, must match this shape —
 * not the trailing Vorhaben suffix (`…/2026-1/1`) and not the UI placeholder `BSFZ-2024-…`.
 */
export const PROJECT_CODE_PATTERN = /^\d{3}-\d{3}-\d{3}\/\d{4}-\d+$/;

export function isValidProjectCode(value: string): boolean {
  return PROJECT_CODE_PATTERN.test(value);
}

/** Trim empty → null; when present, enforce Lfd. Nr. shape (max 64 chars). */
export const projectCodeSchema = z.preprocess(
  (value) => {
    if (value === undefined) return undefined;
    if (value === null) return null;
    if (typeof value !== "string") return value;
    const trimmed = value.trim();
    return trimmed === "" ? null : trimmed;
  },
  z
    .string()
    .max(64)
    .regex(PROJECT_CODE_PATTERN, {
      message: "Project ID must look like a BSFZ Lfd. Nr. (e.g. 666-969-422/2026-1)",
    })
    .nullable()
    .optional(),
);

/** Short BSFZ project title used on the Stundenzettel; empty → null. */
export const bsfzProjectDescriptionSchema = z.preprocess((value) => {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}, z.string().max(2000).nullable().optional());

/** Per-project reply-to; empty → null (V-17). */
export const senderEmailSchema = z.preprocess((value) => {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed.toLowerCase();
}, emailSchema.nullable().optional());

export const projectCreateSchema = z
  .object({
    /**
     * Existing tenant. Empty string / null is treated as omitted so the SPA can send
     * `newCompany` without a dummy UUID. XOR with `newCompany` (both or neither → invalid).
     */
    companyId: z.preprocess(
      (value) => (value === "" || value === null ? undefined : value),
      uuidSchema.optional(),
    ),
    /** Draft tenant created in the same transaction as the project (SPA new-project form). */
    newCompany: companyCreateSchema.optional(),
    name: nameSchema,
    /** Optional on the wire; the server falls back to the company's code (PLAN.md D-29). */
    bundesland: bundeslandSchema.optional(),
    startDate: businessDateSchema,
    endDate: businessDateSchema,
    bsfzProjectDescription: bsfzProjectDescriptionSchema,
    projectCode: projectCodeSchema,
  })
  .refine((value) => value.endDate >= value.startDate, {
    message: "The end date cannot precede the start date",
    path: ["endDate"],
  })
  .refine((value) => Boolean(value.companyId) !== Boolean(value.newCompany), {
    message: "Provide either companyId or newCompany",
    path: ["companyId"],
  });

export const projectPatchSchema = z
  .object({
    name: nameSchema.optional(),
    bundesland: bundeslandSchema.optional(),
    startDate: businessDateSchema.optional(),
    endDate: businessDateSchema.optional(),
    bsfzProjectDescription: bsfzProjectDescriptionSchema,
    projectCode: projectCodeSchema,
    senderEmail: senderEmailSchema,
  })
  .refine((patch) => Object.keys(patch).length > 0, { message: "Nothing to change" });

// --------------------------------------------------------------------- people

/** ISO weekdays, 1 = Monday. Mirrors the `is_valid_weekdays` CHECK on `users`. */
export const workWeekdaysSchema = z
  .array(z.number().int().min(1).max(7))
  .min(1)
  .max(7)
  .refine((days) => new Set(days).size === days.length, { message: "Duplicate weekday" })
  .transform((days) => [...days].sort((a, b) => a - b));

const workProfileSchema = z.object({
  // Absolute ceilings (80 / 7); product maxes come from app_settings.work_profile_limits.
  weeklyHours: z.number().positive().max(80),
  workdaysPerWeek: z.number().int().min(1).max(7),
  workWeekdays: workWeekdaysSchema.default([1, 2, 3, 4, 5]),
});

/** A person and their first project in one call — §4.3 asks for ≥ 1 assignment up front. */
export const employeeCreateSchema = z
  .object({
    firstName: personNameSchema,
    lastName: personNameSchema,
    email: emailSchema,
    position: positionSchema.optional().default(null),
    jobDescription: jobDescriptionSchema.optional().default(null),
    role: z.enum(["company_admin", "employee"]),
    assignment: z.object({
      projectId: uuidSchema,
      entryDate: businessDateSchema,
      rdShare: rdShareSchema,
      leaveDate: businessDateSchema.optional(),
    }),
    /** PLAN.md D-26: the single form invites by default, the importer never does. */
    sendInvite: z.boolean().default(true),
  })
  .and(workProfileSchema);

/** Master data — G-Admin only (matrix #5/#7, PLAN.md D-31). Role lives on its own endpoint. */
export const employeePatchSchema = z
  .object({
    firstName: personNameSchema.optional(),
    lastName: personNameSchema.optional(),
    email: emailSchema.optional(),
    position: positionSchema.optional(),
    jobDescription: jobDescriptionSchema.optional(),
    weeklyHours: z.number().positive().max(80).optional(),
    workdaysPerWeek: z.number().int().min(1).max(7).optional(),
    workWeekdays: workWeekdaysSchema.optional(),
    /** G-Admin disable/enable — sessions revoked when turning off (Phase 5.10). */
    isEnabled: z.boolean().optional(),
  })
  .refine((patch) => Object.keys(patch).length > 0, { message: "Nothing to change" });

export const roleChangeSchema = z.object({
  role: z.enum(["company_admin", "employee"]),
});

// --------------------------------------------------------------------- assignments

export const assignmentCreateSchema = z.object({
  projectId: uuidSchema,
  entryDate: businessDateSchema,
  rdShare: rdShareSchema,
  leaveDate: businessDateSchema.optional(),
  /** Same default as Add Employee (PLAN.md D-26): invite on assign unless unticked. */
  sendInvite: z.boolean().default(true),
});

export const assignmentPatchSchema = z
  .object({
    entryDate: businessDateSchema.optional(),
    rdShare: rdShareSchema.optional(),
    /** `null` clears the Project Leave Date. */
    leaveDate: businessDateSchema.nullable().optional(),
  })
  .refine((patch) => Object.keys(patch).length > 0, { message: "Nothing to change" });

// --------------------------------------------------------------------- import

/** One parsed spreadsheet row after the mapping module has had it (PRD §10.1). */
export const importRowSchema = z.object({
  rowNumber: z.number().int().positive(),
  firstName: personNameSchema,
  lastName: personNameSchema,
  email: emailSchema,
  position: positionSchema.optional().default(null),
  jobDescription: jobDescriptionSchema.optional().default(null),
  weeklyHours: z.number().positive().max(80),
  workdaysPerWeek: z.number().int().min(1).max(7),
  rdShare: rdShareSchema,
  entryDate: businessDateSchema,
  leaveDate: businessDateSchema.nullable().optional(),
  /** Present + boolean when the sheet had `Unternehmens-Admin` (legacy `isCompanyAdmin`); null/omitted = column absent. */
  isCompanyAdmin: z.boolean().nullable().optional(),
});

export const importCommitSchema = z.object({
  filename: z.string().trim().min(1).max(255),
  defaultEntryDate: businessDateSchema,
  rows: z.array(importRowSchema).min(1).max(500),
});

export const invitePeopleSchema = z.object({
  userIds: z.array(uuidSchema).min(1).max(200),
});

/** One accepted hours-import row after dry-run (PRD §10.2). */
export const hoursImportRowSchema = z.object({
  rowNumber: z.number().int().positive(),
  email: emailSchema,
  projectName: z.string().trim().min(1).max(200).nullable(),
  projectId: uuidSchema.nullable(),
  entryDate: businessDateSchema,
  totalHours: z.number().min(0).max(24),
  rdHours: z.number().min(0).max(24),
  absenceType: absenceTypeSchema.nullable(),
  absenceFraction: absenceFractionSchema,
});

export const hoursImportCommitSchema = z.object({
  filename: z.string().trim().min(1).max(255),
  projectId: uuidSchema.nullable().optional(),
  rows: z.array(hoursImportRowSchema).min(1).max(5000),
});

// --------------------------------------------------------------------- responses

/** What the API returns. Dates stay `YYYY-MM-DD` strings; shares stay fractions (D-28). */
/** Slim project row nested on the companies list (reference parity; no fiscal_year — PRD §3). */
export interface CompanyListProject {
  id: string;
  name: string;
  startDate: string;
}

export interface CompanySummary {
  id: string;
  name: string;
  bundesland: Bundesland;
  archived: boolean;
  projectCount: number;
  personCount: number;
  /** Active (non-archived) projects for the list nested sub-rows. */
  projects: CompanyListProject[];
}

export interface ProjectSummary {
  id: string;
  companyId: string;
  companyName: string;
  name: string;
  bundesland: Bundesland | null;
  startDate: string;
  endDate: string;
  /** Short BSFZ title for the Stundenzettel (J4); null when unset. */
  bsfzProjectDescription: string | null;
  /** BSFZ Lfd. Nr. / Vorhaben-ID (J5); null when unset. */
  projectCode: string | null;
  /** Per-project reply-to for invite/reminder mail (V-17); null when unset. */
  senderEmail: string | null;
  archived: boolean;
  personCount: number;
}

export interface CompanyDetail extends CompanySummary {
  projects: ProjectSummary[];
  /** True while tracked data exists below the company, so the UI can pre-empt the 409 (D-23). */
  hasTrackedData: boolean;
}

export interface AssignmentSummary {
  id: string;
  projectId: string;
  projectName: string;
  projectArchived: boolean;
  entryDate: string;
  /** Inclusive last trackable day on this project; null = open until project end. */
  leaveDate: string | null;
  rdShare: number;
  archived: boolean;
  timeEntries: number;
  weekNotes: number;
  /** Decided server-side from the caller's role and the counts — never re-derived in the UI. */
  canEdit: boolean;
  /** CA and G-Admin may always set/clear Project Leave Date. */
  canEditLeaveDate: boolean;
  canRemove: boolean;
}

export interface PersonSummary {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  /** Optional job title; null when unset. */
  position: string | null;
  /** Optional long role description; null when unset. Not shown on list tables. */
  jobDescription: string | null;
  role: "grantonomy_admin" | "company_admin" | "employee";
  weeklyHours: number | null;
  workdaysPerWeek: number | null;
  workWeekdays: number[];
  archived: boolean;
  hasPassword: boolean;
  invitedAt: string | null;
  lastLoginAt: string | null;
  /** Total `auth_tokens` rows with purpose `invite` for this person (including 0). */
  inviteCount: number;
  assignments: AssignmentSummary[];
}

export function isNeverSignedIn(
  person: Pick<PersonSummary, "hasPassword" | "lastLoginAt">,
): boolean {
  return !person.hasPassword && person.lastLoginAt === null;
}

export type ImportVerdict = "create" | "assign" | "skip";

export interface ImportPreviewRow {
  rowNumber: number;
  firstName: string;
  lastName: string;
  email: string;
  position: string | null;
  jobDescription: string | null;
  weeklyHours: number;
  workdaysPerWeek: number;
  rdShare: number;
  entryDate: string;
  /** Inclusive last trackable day; null/omitted when the sheet had no leave date. */
  leaveDate?: string | null;
  verdict: ImportVerdict;
  /** Present + boolean when the sheet had `Unternehmens-Admin` (legacy `isCompanyAdmin`); null = column absent. */
  isCompanyAdmin?: boolean | null;
  /** True when this create-row will be inserted as company_admin (marked 1 or fallback). */
  willBeCompanyAdmin?: boolean;
  /** Why a row is skipped, or which master-data fields differ from the person on file (D-25). */
  reason?: string;
  differs?: string[];
}

export interface ImportFallbackAdmin {
  name: string;
  email: string;
}

export interface ImportPreview {
  filename: string;
  rows: ImportPreviewRow[];
  errors: { rowNumber: number; message: string }[];
  counts: { create: number; assign: number; skip: number };
  /** Set when we picked a company admin because the column was missing or every create was 0. */
  fallbackCompanyAdmin?: ImportFallbackAdmin | null;
}

export interface ImportResult {
  batchId: string;
  created: number;
  assigned: number;
  skipped: number;
  /** Unique never-signed-in, enabled, non-archived people from this commit's create ∪ assign. */
  inviteableUserIds: string[];
}

export type CompanyCreateInput = z.infer<typeof companyCreateSchema>;
export type CompanyPatchInput = z.infer<typeof companyPatchSchema>;
export type ProjectCreateInput = z.infer<typeof projectCreateSchema>;
export type ProjectPatchInput = z.infer<typeof projectPatchSchema>;
export type EmployeeCreateInput = z.infer<typeof employeeCreateSchema>;
export type EmployeePatchInput = z.infer<typeof employeePatchSchema>;
export type RoleChangeInput = z.infer<typeof roleChangeSchema>;
export type AssignmentCreateInput = z.infer<typeof assignmentCreateSchema>;
export type AssignmentPatchInput = z.infer<typeof assignmentPatchSchema>;
export type ImportRow = z.infer<typeof importRowSchema>;
export type ImportCommitInput = z.infer<typeof importCommitSchema>;
export type InvitePeopleInput = z.infer<typeof invitePeopleSchema>;
export type HoursImportRowInput = z.infer<typeof hoursImportRowSchema>;
export type HoursImportCommitInput = z.infer<typeof hoursImportCommitSchema>;
